import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft,
    Loader2,
    Plus,
    Trash2,
    Send,
    ExternalLink,
    FileUp,
    FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { GROWTH_SERVICES, findGrowthService } from "@/lib/growthServices";
import AdminShell from "@/components/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_STATUSES = ["draft", "active", "on_hold", "review", "completed", "archived"];
const MILESTONE_STATUSES = ["pending", "in_progress", "review", "approved", "completed"];

export default function AdminProjectEdit() {
    const { id } = useParams();
    const { user, token, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [comment, setComment] = useState("");
    const [newMilestone, setNewMilestone] = useState({
        title: "",
        description: "",
        requires_approval: false,
    });
    const [newResource, setNewResource] = useState({
        label: "",
        url: "",
        kind: "drive",
    });

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const refresh = () =>
        axios
            .get(`${API}/admin/projects/${id}`, { headers })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Could not load project"))
            .finally(() => setBusy(false));

    useEffect(() => {
        if (token) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, id]);

    if (loading) return null;
    if (!user) return <Navigate to="/admin/login" replace />;
    if (busy)
        return (
            <AdminShell>
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </AdminShell>
        );
    if (!data) return null;

    const { project, milestones, resources, comments, scripts = [] } = data;

    const uploadScript = async (file, label, description) => {
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) {
            toast.error("File must be 25 MB or smaller");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("label", label);
        if (description) fd.append("description", description);
        try {
            await axios.post(`${API}/admin/projects/${id}/scripts`, fd, {
                headers: { ...headers, "Content-Type": "multipart/form-data" },
            });
            toast.success("Script sent — client emailed");
            refresh();
            return true;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
            return false;
        }
    };

    const deleteScript = async (sid) => {
        if (!window.confirm("Delete this script?")) return;
        try {
            await axios.delete(`${API}/admin/scripts/${sid}`, { headers });
            toast.success("Script deleted");
            refresh();
        } catch {
            toast.error("Could not delete");
        }
    };

    const updateProject = async (patch) => {
        try {
            const { data: p } = await axios.patch(
                `${API}/admin/projects/${id}`,
                patch,
                { headers },
            );
            setData((d) => ({ ...d, project: p }));
        } catch {
            toast.error("Could not update project");
        }
    };

    const deleteProject = async () => {
        if (!window.confirm("Delete this project and all its milestones?")) return;
        try {
            await axios.delete(`${API}/admin/projects/${id}`, { headers });
            toast.success("Project deleted");
            navigate(`/admin/clients/${project.client_id}`);
        } catch {
            toast.error("Delete failed");
        }
    };

    const addMilestone = async (e) => {
        e.preventDefault();
        if (!newMilestone.title) return;
        try {
            await axios.post(
                `${API}/admin/projects/${id}/milestones`,
                { ...newMilestone, order: milestones.length },
                { headers },
            );
            setNewMilestone({ title: "", description: "", requires_approval: false });
            refresh();
        } catch {
            toast.error("Could not add milestone");
        }
    };

    const updateMilestone = async (mid, patch) => {
        try {
            await axios.patch(`${API}/admin/milestones/${mid}`, patch, { headers });
            refresh();
        } catch {
            toast.error("Could not update milestone");
        }
    };

    const deleteMilestone = async (mid) => {
        if (!window.confirm("Delete this milestone?")) return;
        try {
            await axios.delete(`${API}/admin/milestones/${mid}`, { headers });
            refresh();
        } catch {
            toast.error("Delete failed");
        }
    };

    const addResource = async (e) => {
        e.preventDefault();
        if (!newResource.label || !newResource.url) return;
        try {
            await axios.post(
                `${API}/admin/projects/${id}/resources`,
                newResource,
                { headers },
            );
            toast.success(
                `Resource added — emailed ${data?.project?.client_email || "the client"}`,
            );
            setNewResource({ label: "", url: "", kind: "drive" });
            refresh();
        } catch {
            toast.error("Could not add resource");
        }
    };

    const deleteResource = async (rid) => {
        try {
            await axios.delete(`${API}/admin/resources/${rid}`, { headers });
            refresh();
        } catch {
            toast.error("Delete failed");
        }
    };

    const postComment = async (e) => {
        e.preventDefault();
        if (!comment.trim()) return;
        try {
            await axios.post(
                `${API}/admin/projects/${id}/comments`,
                { body: comment.trim() },
                { headers },
            );
            setComment("");
            refresh();
        } catch {
            toast.error("Could not post note");
        }
    };

    return (
        <AdminShell>
            <Link
                to={`/admin/clients/${project.client_id}`}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-bone-300 hover:text-ember mb-8"
            >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to client
            </Link>

            <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
                <div>
                    <input
                        defaultValue={project.title}
                        onBlur={(e) =>
                            e.target.value !== project.title &&
                            updateProject({ title: e.target.value })
                        }
                        data-testid="project-title-input"
                        className="font-display text-bone text-4xl lg:text-5xl tracking-tightest leading-[1] bg-transparent outline-none focus:border-b focus:border-ember w-full"
                    />
                    <textarea
                        defaultValue={project.summary || ""}
                        onBlur={(e) =>
                            e.target.value !== (project.summary || "") &&
                            updateProject({ summary: e.target.value })
                        }
                        placeholder="Summary…"
                        data-testid="project-summary-input"
                        rows={2}
                        className="mt-3 text-bone-300 bg-transparent outline-none focus:border-b focus:border-ember w-full max-w-[60ch] resize-none"
                    />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        value={project.status}
                        onChange={(e) =>
                            updateProject({ status: e.target.value })
                        }
                        data-testid="project-status"
                        className="bg-ink-800 border border-white/15 px-3 py-2 text-xs uppercase tracking-[0.2em] text-bone"
                    >
                        {PROJECT_STATUSES.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={deleteProject}
                        data-testid="project-delete"
                        className="inline-flex items-center gap-2 h-10 px-3 border border-white/15 hover:border-red-400 hover:text-red-400 text-xs uppercase tracking-[0.2em]"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Growth services picker */}
            <div
                data-testid="project-services-editor"
                className="mb-10 pb-8 border-b border-white/10"
            >
                <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                    Growth services (shown to client + manager)
                </p>
                <div className="flex flex-wrap gap-2">
                    {GROWTH_SERVICES.map((s) => {
                        const activeLabels = (project.services || []).map(
                            (v) => findGrowthService(v)?.label || v,
                        );
                        const active = activeLabels.includes(s.label);
                        return (
                            <button
                                key={s.id}
                                type="button"
                                data-testid={`project-service-toggle-${s.id}`}
                                onClick={() => {
                                    const current = (project.services || []).slice();
                                    const idx = current.findIndex(
                                        (v) =>
                                            v === s.label ||
                                            v === s.id ||
                                            findGrowthService(v)?.label === s.label,
                                    );
                                    if (idx >= 0) current.splice(idx, 1);
                                    else current.push(s.label);
                                    updateProject({ services: current });
                                }}
                                className={`px-3 py-2 border text-xs uppercase tracking-[0.18em] transition-colors ${
                                    active
                                        ? "border-ember bg-ember/10 text-ember"
                                        : "border-white/15 text-bone-300 hover:border-white/30"
                                }`}
                            >
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Milestones */}
                <section className="lg:col-span-8">
                    <h2 className="font-display text-bone text-xl tracking-tight pb-3 mb-5 border-b border-white/10">
                        Milestones
                    </h2>
                    <ol className="space-y-3 mb-6">
                        {milestones.map((m, i) => (
                            <li
                                key={m.id}
                                data-testid={`admin-milestone-${m.id}`}
                                className="border border-white/10 bg-ink-800/40 p-5"
                            >
                                <div className="flex items-start gap-3">
                                    <span className="font-display text-bone-500 text-sm tracking-[0.2em] mt-1">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div className="flex-1">
                                        <input
                                            defaultValue={m.title}
                                            onBlur={(e) =>
                                                e.target.value !== m.title &&
                                                updateMilestone(m.id, {
                                                    title: e.target.value,
                                                })
                                            }
                                            className="font-display text-bone text-lg tracking-tight bg-transparent outline-none w-full"
                                        />
                                        <textarea
                                            defaultValue={m.description || ""}
                                            onBlur={(e) =>
                                                e.target.value !==
                                                    (m.description || "") &&
                                                updateMilestone(m.id, {
                                                    description: e.target.value,
                                                })
                                            }
                                            placeholder="Description…"
                                            rows={2}
                                            className="mt-1 text-bone-300 text-sm bg-transparent outline-none w-full resize-none"
                                        />
                                        <div className="mt-3 flex items-center gap-3 flex-wrap">
                                            <select
                                                value={m.status}
                                                onChange={(e) =>
                                                    updateMilestone(m.id, {
                                                        status: e.target.value,
                                                    })
                                                }
                                                className="bg-ink-800 border border-white/15 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-bone"
                                            >
                                                {MILESTONE_STATUSES.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s.replace("_", " ")}
                                                    </option>
                                                ))}
                                            </select>
                                            <label className="text-[10px] uppercase tracking-[0.2em] text-bone-500 inline-flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(
                                                        m.requires_approval,
                                                    )}
                                                    onChange={(e) =>
                                                        updateMilestone(m.id, {
                                                            requires_approval:
                                                                e.target.checked,
                                                        })
                                                    }
                                                />
                                                Needs approval
                                            </label>
                                            {m.approved_by_client && (
                                                <span className="text-[10px] text-emerald-400 uppercase tracking-[0.2em]">
                                                    Approved by client
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => deleteMilestone(m.id)}
                                        className="text-bone-500 hover:text-red-400"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ol>
                    <form
                        onSubmit={addMilestone}
                        className="border border-dashed border-white/15 p-5"
                    >
                        <input
                            value={newMilestone.title}
                            onChange={(e) =>
                                setNewMilestone((s) => ({
                                    ...s,
                                    title: e.target.value,
                                }))
                            }
                            placeholder="New milestone title…"
                            data-testid="new-milestone-title"
                            className="w-full bg-transparent outline-none border-b border-white/15 focus:border-ember py-2 text-bone font-display"
                        />
                        <textarea
                            value={newMilestone.description}
                            onChange={(e) =>
                                setNewMilestone((s) => ({
                                    ...s,
                                    description: e.target.value,
                                }))
                            }
                            rows={2}
                            placeholder="Description (optional)"
                            className="w-full mt-3 bg-transparent outline-none text-bone-300 text-sm resize-none"
                        />
                        <div className="mt-3 flex items-center justify-between">
                            <label className="text-[10px] uppercase tracking-[0.2em] text-bone-500 inline-flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={newMilestone.requires_approval}
                                    onChange={(e) =>
                                        setNewMilestone((s) => ({
                                            ...s,
                                            requires_approval: e.target.checked,
                                        }))
                                    }
                                />
                                Needs client approval
                            </label>
                            <button
                                type="submit"
                                data-testid="add-milestone"
                                className="btn-ember inline-flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.2em]"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add milestone
                            </button>
                        </div>
                    </form>

                    {/* Comments */}
                    <h2 className="mt-12 font-display text-bone text-xl tracking-tight pb-3 mb-5 border-b border-white/10">
                        Notes
                    </h2>
                    <form
                        onSubmit={postComment}
                        className="flex items-start gap-3 mb-5 border border-white/10 bg-ink-800/40 p-4"
                    >
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={2}
                            placeholder="Note for the client…"
                            data-testid="admin-comment-input"
                            className="flex-1 bg-transparent outline-none text-sm text-bone resize-none"
                        />
                        <button
                            type="submit"
                            data-testid="admin-comment-submit"
                            className="btn-ember inline-flex items-center gap-2 h-10 px-4 text-xs uppercase tracking-[0.2em]"
                        >
                            <Send className="w-3.5 h-3.5" />
                            Send
                        </button>
                    </form>
                    <ul className="space-y-3">
                        {comments
                            .slice()
                            .reverse()
                            .map((c) => (
                                <li
                                    key={c.id}
                                    className={`p-4 border ${
                                        c.author_type === "client"
                                            ? "border-ember/30 bg-ember/5"
                                            : "border-white/10 bg-ink-800/40"
                                    }`}
                                >
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="font-display text-bone">
                                            {c.author_name}
                                            {c.author_type === "client" && (
                                                <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-ember">
                                                    Client
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-bone-500">
                                            {new Date(
                                                c.created_at,
                                            ).toLocaleString()}
                                        </span>
                                    </div>
                                    <p className="text-bone-300 text-sm whitespace-pre-wrap">
                                        {c.body}
                                    </p>
                                </li>
                            ))}
                    </ul>
                </section>

                {/* Sidebar: resources */}
                <aside className="lg:col-span-4">
                    <h2 className="font-display text-bone text-xl tracking-tight pb-3 mb-5 border-b border-white/10">
                        Resources
                    </h2>
                    <ul className="space-y-3 mb-5">
                        {resources.map((r) => (
                            <li
                                key={r.id}
                                className="flex items-start justify-between gap-3 p-3 border border-white/10"
                            >
                                <a
                                    href={r.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 group"
                                >
                                    <div className="text-bone group-hover:text-ember inline-flex items-center gap-2">
                                        {r.label}
                                        <ExternalLink className="w-3 h-3" />
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-bone-500">
                                        {r.kind}
                                    </div>
                                </a>
                                <button
                                    onClick={() => deleteResource(r.id)}
                                    className="text-bone-500 hover:text-red-400"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </li>
                        ))}
                    </ul>
                    <form
                        onSubmit={addResource}
                        className="space-y-3 border border-dashed border-white/15 p-4"
                    >
                        <input
                            value={newResource.label}
                            onChange={(e) =>
                                setNewResource((s) => ({
                                    ...s,
                                    label: e.target.value,
                                }))
                            }
                            placeholder="Label (e.g., Brand assets)"
                            data-testid="new-resource-label"
                            className="w-full bg-transparent outline-none border-b border-white/15 focus:border-ember py-2 text-bone text-sm"
                        />
                        <input
                            value={newResource.url}
                            onChange={(e) =>
                                setNewResource((s) => ({
                                    ...s,
                                    url: e.target.value,
                                }))
                            }
                            placeholder="https://drive.google.com/…"
                            data-testid="new-resource-url"
                            className="w-full bg-transparent outline-none border-b border-white/15 focus:border-ember py-2 text-bone text-sm"
                        />
                        <select
                            value={newResource.kind}
                            onChange={(e) =>
                                setNewResource((s) => ({
                                    ...s,
                                    kind: e.target.value,
                                }))
                            }
                            className="w-full bg-ink-800 border border-white/15 p-2 text-xs uppercase tracking-[0.2em] text-bone"
                        >
                            {["drive", "dropbox", "figma", "loom", "notion", "link"].map(
                                (k) => (
                                    <option key={k} value={k}>
                                        {k}
                                    </option>
                                ),
                            )}
                        </select>
                        <button
                            type="submit"
                            data-testid="add-resource"
                            className="btn-ember w-full inline-flex items-center justify-center gap-2 h-10 text-xs uppercase tracking-[0.2em]"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Add link
                        </button>
                    </form>

                    <ScriptPanel
                        projectId={id}
                        scripts={scripts}
                        onUpload={uploadScript}
                        onDelete={deleteScript}
                    />
                </aside>
            </div>
        </AdminShell>
    );
}

function ScriptPanel({ projectId, scripts, onUpload, onDelete }) {
    const [file, setFile] = useState(null);
    const [label, setLabel] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Pick a file first");
            return;
        }
        if (!label.trim()) {
            toast.error("Add a label so the client knows what this is");
            return;
        }
        setBusy(true);
        const ok = await onUpload(file, label.trim(), description.trim());
        setBusy(false);
        if (ok) {
            setFile(null);
            setLabel("");
            setDescription("");
            const input = document.querySelector('[data-testid="script-file-input"]');
            if (input) input.value = "";
        }
    };

    return (
        <div className="mt-10">
            <h2 className="font-display text-bone text-xl tracking-tight pb-3 mb-5 border-b border-white/10 flex items-center gap-2">
                <FileText className="w-4 h-4 text-ember" />
                Scripts
            </h2>
            <ul className="space-y-3 mb-5" data-testid="admin-scripts-list">
                {scripts.length === 0 && (
                    <p className="text-bone-500 text-xs">
                        Upload a PDF or doc — your client will get inline preview + can leave feedback below it.
                    </p>
                )}
                {scripts.map((s) => (
                    <li
                        key={s.id}
                        data-testid={`admin-script-${s.id}`}
                        className="border border-white/10 p-3"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="font-display text-bone text-sm tracking-tight truncate">
                                    {s.label}
                                </p>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-bone-500 mt-1">
                                    {s.filename} · {(s.size / 1024).toFixed(0)} KB
                                </p>
                            </div>
                            <button
                                onClick={() => onDelete(s.id)}
                                className="text-bone-500 hover:text-red-400 shrink-0"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
            <form
                onSubmit={onSubmit}
                data-testid="script-upload-form"
                className="space-y-3 border border-dashed border-white/15 p-4"
            >
                <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Label (e.g. Brand voice script v2)"
                    data-testid="script-label"
                    className="w-full bg-transparent outline-none border-b border-white/15 focus:border-ember py-2 text-bone text-sm"
                />
                <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Note (optional)"
                    className="w-full bg-transparent outline-none border-b border-white/15 focus:border-ember py-2 text-bone text-sm"
                />
                <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.md,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="script-file-input"
                    className="w-full text-bone-300 text-xs file:mr-4 file:py-2 file:px-3 file:border file:border-white/15 file:bg-transparent file:text-bone-300 hover:file:border-ember/40"
                />
                <p className="text-[10px] tracking-[0.18em] uppercase text-bone-500">
                    PDF / Word / TXT — max 25 MB
                </p>
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="script-upload-submit"
                    className="btn-ember w-full inline-flex items-center justify-center gap-2 h-10 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
                >
                    {busy ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                        <>
                            <FileUp className="w-3.5 h-3.5" />
                            Send script
                        </>
                    )}
                </button>
            </form>
        </div>
    );
}
